import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tabs, 
  Button, 
  message, 
  Spin, 
  Space, 
  Popconfirm,
  Checkbox,
  Row,
  Col,
  Tag,
  Modal,
  Image,
  Input
} from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, SyncOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { TabPane } = Tabs;

const EventComplete = ({ user }) => {
  const { apiClient } = useAuth();
  const [activeTab, setActiveTab] = useState('2'); // 默认显示待审批
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // 批量选择的行
  const [refreshFlag, setRefreshFlag] = useState(0); // 刷新标志
  const [previewVisible, setPreviewVisible] = useState(false); // 图片预览弹窗显示状态
  const [previewImages, setPreviewImages] = useState([]); // 预览的图片列表
  const [filteredData, setFilteredData] = useState([]); // 过滤后的数据
  const [filters, setFilters] = useState({}); // 过滤条件

  // 打开图片预览弹窗
  const handlePreviewImages = (picUrls) => {
    if (!picUrls) return;
    const images = picUrls.split(',').filter(url => url.trim());
    setPreviewImages(images);
    setPreviewVisible(true);
  };

  // 关闭图片预览弹窗
  const handleClosePreview = () => {
    setPreviewVisible(false);
    setPreviewImages([]);
  };

  // 获取当前登录用户信息
  const getCurrentUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('解析用户信息失败:', e);
      return null;
    }
  };

  // 根据状态码获取状态描述
  const getStateDesc = (state) => {
    switch(state) {
      case '0': return '有效';
      case '1': return '已拒绝';
      case '2': return '审批中';
      default: return '未知';
    }
  };

  // 根据状态码获取标签颜色
  const getStateColor = (state) => {
    switch(state) {
      case '0': return 'green';
      case '1': return 'red';
      case '2': return 'orange';
      default: return 'default';
    }
  };

  // 获取数据
  const fetchData = async (pageNo = 1, pageSize = 10, state = '2') => {
    if (!apiClient) return;
    
    setLoading(true);
    try {
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/event/complete/pageList', {
        state: state,
        pageSize: pageSize,
        pageNo: pageNo
      });

      if (response.data && response.data.code === 200) {
        const list = response.data.data?.list || [];
        const totalRecord = response.data.data?.totalRecord || 0;
        
        setData(list);
        setFilteredData(list); // 初始化过滤数据
        setPagination(prev => ({
          ...prev,
          current: pageNo,
          pageSize: pageSize,
          total: totalRecord
        }));
      } else {
        message.error(response.data?.msg || '获取数据失败');
        setData([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败，请稍后重试');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理Tab切换
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedRowKeys([]); // 切换Tab时清空选择
    setFilters({}); // 清空过滤条件
    fetchData(1, pagination.pageSize, key);
  };

  // 处理过滤
  const handleFilter = (dataIndex, value) => {
    const newFilters = { ...filters, [dataIndex]: value };
    setFilters(newFilters);
    
    let filtered = [...data];
    Object.keys(newFilters).forEach(key => {
      const filterValue = newFilters[key];
      if (filterValue) {
        filtered = filtered.filter(record => {
          const recordValue = record[key];
          if (recordValue === undefined || recordValue === null) return false;
          return String(recordValue).toLowerCase().includes(String(filterValue).toLowerCase());
        });
      }
    });
    setFilteredData(filtered);
  };

  // 清除过滤
  const clearFilter = (dataIndex) => {
    const newFilters = { ...filters };
    delete newFilters[dataIndex];
    setFilters(newFilters);
    
    let filtered = [...data];
    Object.keys(newFilters).forEach(key => {
      const filterValue = newFilters[key];
      if (filterValue) {
        filtered = filtered.filter(record => {
          const recordValue = record[key];
          if (recordValue === undefined || recordValue === null) return false;
          return String(recordValue).toLowerCase().includes(String(filterValue).toLowerCase());
        });
      }
    });
    setFilteredData(filtered);
  };

  // 过滤下拉框组件
  const FilterDropdown = ({ dataIndex, placeholder }) => ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => {
          handleFilter(dataIndex, selectedKeys[0]);
          confirm();
        }}
        style={{ width: 188, marginBottom: 8, display: 'block' }}
      />
      <Button
        type="primary"
        onClick={() => {
          handleFilter(dataIndex, selectedKeys[0]);
          confirm();
        }}
        size="small"
        style={{ width: 90, marginRight: 8 }}
      >
        搜索
      </Button>
      <Button 
        onClick={() => {
          clearFilter(dataIndex);
          clearFilters();
        }} 
        size="small" 
        style={{ width: 90 }}
      >
        重置
      </Button>
    </div>
  );

  // 过滤图标
  const FilterIcon = filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;

  // 处理分页变化
  const handleTableChange = (paginationObj) => {
    fetchData(paginationObj.current, paginationObj.pageSize, activeTab);
  };

  // 处理单个审批操作
  const handleApprove = async (record, state) => {
    if (!apiClient) return;
    
    try {
      const currentUser = getCurrentUserInfo();
      const updateUser = currentUser?.name || '';
      
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/event/complete/approve', {
        id: record.id,
        state: state,
        updateUser: updateUser,
        userId: record.userId,
        userName: record.userName,
        point: record.point,
        createUser: record.createUser,
        eventId: record.eventId
      });

      if (response.data && response.data.code === 200) {
        message.success(state === '0' ? '同意成功' : '驳回成功');
        // 刷新当前页数据
        fetchData(pagination.current, pagination.pageSize, activeTab);
      } else {
        message.error(response.data?.msg || '操作失败');
      }
    } catch (error) {
      console.error('审批操作失败:', error);
      message.error('操作失败，请稍后重试');
    }
  };

  // 处理批量审批
  const handleBatchApprove = async (state) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要操作的数据');
      return;
    }

    try {
      const currentUser = getCurrentUserInfo();
      const updateUser = currentUser?.name || '';
      
      // 批量处理
      const promises = selectedRowKeys.map(id => {
        const record = data.find(item => item.id === id);
        if (record) {
          return apiClient.post('/sports-server-prod/taller.api.v1/event/complete/approve', {
            id: record.id,
            state: state,
            updateUser: updateUser,
            userId: record.userId,
            userName: record.userName,
            point: record.point,
            createUser: record.createUser,
            eventId: record.eventId
          });
        }
        return Promise.resolve();
      });

      const results = await Promise.all(promises);
      
      // 检查结果
      const successCount = results.filter(result => 
        result?.data?.code === 200
      ).length;
      
      if (successCount === selectedRowKeys.length) {
        message.success(state === '0' ? `批量同意成功(${successCount}条)` : `批量驳回成功(${successCount}条)`);
      } else {
        message.warning(`部分操作成功，成功${successCount}条，失败${selectedRowKeys.length - successCount}条`);
      }
      
      // 清空选择并刷新数据
      setSelectedRowKeys([]);
      fetchData(pagination.current, pagination.pageSize, activeTab);
    } catch (error) {
      console.error('批量审批失败:', error);
      message.error('批量操作失败，请稍后重试');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '任务分组',
      dataIndex: 'groupNo',
      key: 'groupNo',
      width: 120,
      filterDropdown: FilterDropdown({ dataIndex: 'groupNo', placeholder: '搜索任务分组' }),
      filterIcon: FilterIcon,
      onFilter: (value, record) => {
        const groupNo = record.groupNo || '';
        return groupNo.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '任务名称',
      dataIndex: 'eventName',
      key: 'eventName',
      width: 200,
      ellipsis: true,
      filterDropdown: FilterDropdown({ dataIndex: 'eventName', placeholder: '搜索任务名称' }),
      filterIcon: FilterIcon,
      onFilter: (value, record) => {
        const eventName = record.eventName || '';
        return eventName.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '校区',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 180,
      ellipsis: true,
      filterDropdown: FilterDropdown({ dataIndex: 'customerName', placeholder: '搜索校区' }),
      filterIcon: FilterIcon,
      onFilter: (value, record) => {
        const customerName = record.customerName || '';
        return customerName.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: '用户名',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
      filterDropdown: FilterDropdown({ dataIndex: 'userName', placeholder: '搜索用户名' }),
      filterIcon: FilterIcon,
      onFilter: (value, record) => {
        const userName = record.userName || '';
        return userName.toLowerCase().includes(value.toLowerCase());
      },
    },

    {
      title: '积分',
      dataIndex: 'point',
      key: 'point',
      width: 80,
      render: (text) => text || 0,
    },
    {
      title: '状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => (
        <Tag color={getStateColor(state)}>
          {getStateDesc(state)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createDate',
      key: 'createDate',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '上传材料',
      dataIndex: 'picUrls',
      key: 'picUrls',
      width: 100,
      render: (picUrls) => (
        picUrls ? (
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handlePreviewImages(picUrls)}
          >
            查看
          </Button>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      ),
    },
  ];

  // 待审批Tab的操作列
  const pendingColumns = [
    ...columns,
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title="确认同意"
            description="确定要同意这条任务完成记录吗？"
            onConfirm={() => handleApprove(record, '0')}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" size="small" icon={<CheckOutlined />}>
              同意
            </Button>
          </Popconfirm>
          <Popconfirm
            title="确认驳回"
            description="确定要驳回这条任务完成记录吗？"
            onConfirm={() => handleApprove(record, '1')}
            okText="确定"
            cancelText="取消"
          >
            <Button danger size="small" icon={<CloseOutlined />}>
              驳回
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 已完成和已驳回Tab的列（无操作列）
  const completedColumns = columns;

  // 表格行选择配置
  const rowSelection = activeTab === '2' ? {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.state !== '2', // 只有待审批状态可以被选择
    }),
  } : null;

  // 页面加载时获取数据
  useEffect(() => {
    fetchData(1, pagination.pageSize, activeTab);
  }, [apiClient, refreshFlag]);

  return (
    <div style={{ padding: 16 }}>
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          tabBarExtraContent={
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {activeTab === '2' && selectedRowKeys.length > 0 && (
                <Space>
                  <Popconfirm
                    title="确认批量同意"
                    description={`确定要同意选中的${selectedRowKeys.length}条记录吗？`}
                    onConfirm={() => handleBatchApprove('0')}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="primary" icon={<CheckOutlined />}>
                      批量同意({selectedRowKeys.length})
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="确认批量驳回"
                    description={`确定要驳回选中的${selectedRowKeys.length}条记录吗？`}
                    onConfirm={() => handleBatchApprove('1')}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button danger icon={<CloseOutlined />}>
                      批量驳回({selectedRowKeys.length})
                    </Button>
                  </Popconfirm>
                </Space>
              )}
              <Button 
                icon={<SyncOutlined />} 
                onClick={() => {
                  setSelectedRowKeys([]);
                  setRefreshFlag(prev => prev + 1);
                }}
              >
                刷新
              </Button>
            </div>
          }
        >
          <TabPane tab="待审批" key="2">
            <Table
              rowKey="id"
              loading={loading}
              columns={pendingColumns}
              dataSource={filteredData}
              pagination={pagination}
              onChange={handleTableChange}
              rowSelection={rowSelection}
              scroll={{ x: 800 }}
            />
          </TabPane>
          
          <TabPane tab="已完成" key="0">
            <Table
              rowKey="id"
              loading={loading}
              columns={completedColumns}
              dataSource={filteredData}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
            />
          </TabPane>
          
          <TabPane tab="已驳回" key="1">
            <Table
              rowKey="id"
              loading={loading}
              columns={completedColumns}
              dataSource={filteredData}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 800 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 图片预览弹窗 */}
      <Modal
        title="上传材料"
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={null}
        width={800}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 600, overflowY: 'auto' }}>
          {previewImages.map((url, index) => (
            <div key={index} style={{ textAlign: 'center' }}>
              <Image
                src={url.trim()}
                alt={`图片${index + 1}`}
                style={{ maxWidth: '100%', maxHeight: 400 }}
                placeholder={<Spin />}
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default EventComplete;